import clsx from 'clsx';
import EmptyList from 'components/Bookings2/UI/EmptyList';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { Table } from 'components/UI/V3/Table/Table';
import { OpsTask, OpsTaskStatusId } from 'model/OpsTask';
import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { OpsTaskStatusOption } from './OpsTaskStatusSelect';
import OpsTasksTableRow from './OpsTasksTableRow';
import styles from './OpsTasksTable.module.scss';
import { TableColumnSort } from 'utils/tableSort';

type EmptyVariant = 'upcoming' | 'completed' | 'all';

export type OpsTasksSortField = 'task' | 'date' | 'room' | 'staff';

const OPS_TASKS_COLUMN_COUNT = 6;

interface OpsTasksTablePagination {
    page: number;
    size: number;
    totalPages: number;
}

interface OpsTasksTableProps {
    tasks: Array<OpsTask>;
    statusOptions: Array<OpsTaskStatusOption>;
    isLoading?: boolean;
    showLocationColumn?: boolean;
    showCompleteAll?: boolean;
    emptyVariant?: EmptyVariant;
    pagination?: OpsTasksTablePagination;
    className?: string;
    sort?: TableColumnSort<OpsTasksSortField>;
    onSort?: (field: OpsTasksSortField) => void;
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
    onEdit?: (task: OpsTask) => void;
    onDelete?: (task: OpsTask) => void;
    onChangeStatus?: (task: OpsTask, status: OpsTaskStatusId) => void;
    onCompleteAllClick?: () => void;
}

export const OpsTasksTable: FunctionComponent<OpsTasksTableProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    if (!props.isLoading && props.tasks.length === 0) {
        return <EmptyList variant={props.emptyVariant ?? 'upcoming'} />;
    }

    return (
        <Table
            className={clsx(props.className, styles.table)}
            fullHeight={true}
            pagination={props.pagination}
            onPageChange={page => props.onPageChange?.(page)}
            onSizeChange={size => props.onSizeChange?.(Number(size))}
        >
            {!isMobile && (
                <TableHeader>
                    <TableHeaderCell
                        sortDirection={props.sort?.field === 'date' ? props.sort.direction : undefined}
                        onSort={() => props.onSort?.('date')}
                    >
                        Date &amp; Time
                    </TableHeaderCell>
                    <TableHeaderCell
                        sortDirection={props.sort?.field === 'task' ? props.sort.direction : undefined}
                        onSort={() => props.onSort?.('task')}
                    >
                        Task
                    </TableHeaderCell>
                    <TableHeaderCell
                        sortDirection={props.sort?.field === 'staff' ? props.sort.direction : undefined}
                        onSort={() => props.onSort?.('staff')}
                    >
                        Staff
                    </TableHeaderCell>
                    <TableHeaderCell
                        sortDirection={props.sort?.field === 'room' ? props.sort.direction : undefined}
                        onSort={() => props.onSort?.('room')}
                    >
                        Room
                    </TableHeaderCell>
                    <TableHeaderCell aria-label="Status" />
                    <TableHeaderCell style={{ textAlign: 'right' }}>
                        {props.showCompleteAll && (
                            <Button
                                label="Complete All"
                                variant="outlined"
                                size="small"
                                className={styles.completeAllButton}
                                onClick={() => props.onCompleteAllClick?.()}
                            />
                        )}
                    </TableHeaderCell>
                </TableHeader>
            )}
            <TableBody>
                {props.isLoading ? (
                    <TableRow>
                        <TableCell
                            colSpan={OPS_TASKS_COLUMN_COUNT}
                            className={styles.spinnerContainer}
                        >
                            <Spinner />
                        </TableCell>
                    </TableRow>
                ) : (
                    props.tasks.map(task => (
                        <OpsTasksTableRow
                            key={task.id}
                            task={task}
                            statusOptions={props.statusOptions}
                            showLocationColumn={props.showLocationColumn}
                            onEdit={props.onEdit}
                            onDelete={props.onDelete}
                            onChangeStatus={props.onChangeStatus}
                        />
                    ))
                )}
            </TableBody>
        </Table>
    );
};

export default OpsTasksTable;
